import { formatDistanceToNowStrict, formatISO, secondsToMilliseconds } from "date-fns";
import { Link, useParams } from "react-router-dom";
import {
  DealActivityFragmentFragment,
  DealActivityType,
  TaskPriority,
  TaskStatus,
} from "../../graphql/generated";
import { classNames } from "../../utils/cn";
import { FileLink } from "./FileLink";
import { FilePill } from "../FilePill";
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  XCircleIcon, PencilSquareIcon
} from "@heroicons/react/20/solid";
import { Pills } from "../Pills";
import { UserPill } from "../UserPill";
import {
  TaskPriorityIcon,
  TaskPriorityPill,
} from "../TaskPriorityPill";
import {
  UserIcon,
  FolderIcon,
  LockClosedIcon,
  LockOpenIcon,
  DocumentPlusIcon,
  ExclamationTriangleIcon,
  FolderPlusIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { BotIcon, CheckSquareIcon, EyeIcon, MergeIcon, MessageCircleCheckIcon, MessageCircleIcon } from "lucide-react";
import { CircleCheckIcon, CircleIcon, PlayCircleIcon } from "lucide-react";
import { formatEnum } from "../../utils/enums";
import { renderMentions } from "../../utils/renderMentions";

import { useState } from "react";

export type ActivityItemMode = "compact" | "verbose";

interface ActivityItemProps {
  activity: DealActivityFragmentFragment;
  isFirst: boolean;
  isLast: boolean;
  mode?: ActivityItemMode;
}

export function ActivityItem(props: ActivityItemProps) {
  const { dealId } = useParams<{ dealId: string }>();

  if (props.activity.type === DealActivityType.FileCreated) {
    if (!props.activity.file) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <DocumentPlusIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                created a new document
              </p>
              <FileLink
                id={props.activity.file.id}
                name={props.activity.file.name}
                folderId={props.activity.file.folderId}
              />
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.FileDeleted) {
    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <TrashIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                deleted {props.activity.file ? "" : "a file"}
              </p>
              {props.activity.file ? (
                <FileLink
                  id={props.activity.file.id}
                  name={props.activity.file.name}
                  folderId={props.activity.file.folderId}
                />
              ) : null}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.FolderCreated) {
    if (!props.activity.folder) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <FolderPlusIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                created a new folder
              </p>

              {/* if the folder is deleted, show the folder name in gray and don't link to it */}
              {props.activity.folder.deletedAt ? (
                <div className="cursor-pointer hover:border-concrete-400 bg-white hover:shadow-sm relative flex items-center rounded-2xl border px-2 py-1">
                  <FolderIcon
                    className={classNames(
                      props.activity.folder.dealGroup
                        ? "text-orange-800/70"
                        : "text-blue-700/70",
                      "w-4 h-4",
                    )}
                  />
                  <p className="text-xs ml-1 truncate">
                    {props.activity.folder.name}
                  </p>
                </div>
              ) : (
                <Link
                  to={`/deal/${dealId}/documents/folder/${props.activity.folder.id}`}
                >
                  <div className="cursor-pointer hover:border-concrete-400 bg-white hover:shadow-sm relative flex items-center rounded-2xl border px-2 py-1">
                    <FolderIcon
                      className={classNames(
                        props.activity.folder.dealGroup
                          ? "text-orange-800/70"
                          : "text-blue-700/70",
                        "w-4 h-4",
                      )}
                    />
                    <p className="text-xs ml-1 truncate">
                      {props.activity.folder.name}
                    </p>
                  </div>
                </Link>
              )}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.FolderDeleted) {
    if (!props.activity.folder) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <FolderPlusIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                deleted a folder
              </p>

              <div className="cursor-pointer hover:border-concrete-400 bg-white hover:shadow-sm relative flex items-center rounded-2xl border px-2 py-1">
                <FolderIcon
                  className={classNames(
                    props.activity.folder.dealGroup
                      ? "text-orange-800/70"
                      : "text-blue-700/70",
                    "w-4 h-4",
                  )}
                />
                <p className="text-xs ml-1 truncate">
                  {props.activity.folder.name}
                </p>
              </div>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.FileVersionCreated) {
    if (!props.activity.file) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <div className="h-1.5 w-1.5 rounded-full bg-persian-300 ring-1 ring-persian-400" />
          </div>

          <div className="flex justify-between w-full gap-x-2 items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                uploaded a new version of
              </p>
              <FileLink
                id={props.activity.file.id}
                name={props.activity.file.name}
                folderId={props.activity.file.folderId}
              />
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.FileVersionDeleted) {
    if (!props.activity.file) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <div className="h-1.5 w-1.5 rounded-full bg-persian-300 ring-1 ring-persian-400" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                deleted a version of
              </p>
              <FileLink
                id={props.activity.file.id}
                name={props.activity.file.name}
                folderId={props.activity.file.folderId}
              />
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.FileDownloaded) {
    if (!props.activity.file) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <ArrowDownTrayIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 ">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                downloaded
              </p>
              <FileLink
                id={props.activity.file.id}
                name={props.activity.file.name}
                folderId={props.activity.file.folderId}
              />
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionViewed) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <EyeIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 ">
              {props.mode === "verbose" ? (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  viewed{" "}
                  <Link
                    to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                  >
                    <span className="font-semibold text-persian-800 hover:text-persian-700">
                      {props.activity.question.title}
                    </span>
                  </Link>
                </p>
              ) : (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  viewed the question
                </p>
              )}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionAsked) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          {props.mode === "verbose" ? (
            <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
              <QuestionMarkCircleIcon className="w-4 h-4 text-persian-600" />
            </div>
          ) : (
            <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
              <div className="h-1.5 w-1.5 rounded-full bg-persian-300 ring-1 ring-persian-400" />
            </div>
          )}

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center gap-x-2 -py-0.5">
              <p className="text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                asked{" "}
                <Link
                  to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                  className="font-semibold text-persian-800 hover:text-persian-700"
                >
                  {props.activity.question.title}
                </Link>
              </p>
            </div>
            <time
              title={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionDocumentsFound) {
    return (
      <QuestionDocumentsFoundItem
        activity={props.activity}
        isLast={props.isLast}
        dealId={dealId}
      />
    );
  }

  if (props.activity.type === DealActivityType.QuestionAnswered) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <MessageCircleCheckIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              {props.mode === "verbose" ? (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  answered{" "}
                  <Link
                    to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                  >
                    <span className="font-semibold text-persian-800 hover:text-persian-700">
                      {props.activity.question.title}
                    </span>
                  </Link>
                </p>
              ) : (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  answered the question
                </p>
              )}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionAnswerUpdated) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <PencilSquareIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                updated the answer for{" "}
                <Link
                  to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                >
                  <span className="font-semibold text-persian-800 hover:text-persian-700">
                    {props.activity.question.title}
                  </span>
                </Link>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionAnswerApproved) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <CheckCircleIcon className="w-4 h-4 text-green-500/80" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              {props.mode === "verbose" ? (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  approved the answer for{" "}
                  <Link
                    to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                  >
                    <span className="font-semibold text-persian-800 hover:text-persian-700">
                      {props.activity.question.title}
                    </span>
                  </Link>
                </p>
              ) : (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  approved the answer
                </p>
              )}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionAnswerRejected) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <XCircleIcon className="w-4 h-4 text-red-500/80" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              {props.mode === "verbose" ? (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  rejected the answer for{" "}
                  <Link
                    to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                  >
                    <span className="font-semibold text-persian-800 hover:text-persian-700">
                      {props.activity.question.title}
                    </span>
                  </Link>
                </p>
              ) : (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  rejected the answer
                </p>
              )}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionAnswerStatusCleared) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <XCircleIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              {props.mode === "verbose" ? (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  cleared the answer status for{" "}
                  <Link
                    to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                  >
                    <span className="font-semibold text-persian-800 hover:text-persian-700">
                      {props.activity.question.title}
                    </span>
                  </Link>
                </p>
              ) : (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  cleared the answer status
                </p>
              )}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.QuestionUpdated) {
    if (!props.activity.question) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <PencilSquareIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              {props.mode === "verbose" ? (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  updated the question{" "}
                  <Link
                    to={`/deal/${dealId}/questions/${props.activity.question.id}`}
                  >
                    <span className="font-semibold text-persian-800 hover:text-persian-700">
                      {props.activity.question.title}
                    </span>
                  </Link>
                </p>
              ) : (
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  updated the question
                </p>
              )}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.TaskCreated) {
    if (!props.activity.task) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1 items-center">
        <div
          className={classNames(
            props.isLast ? "h-6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <CheckSquareIcon className="w-4 h-4 text-persian-600" />
        </div>
        <div className="flex justify-between w-full items-center">
          <div className="flex items-center gap-x-2">
            <p className="text-sm leading-5 text-persian-990">
              <span className="font-semibold text-persian-990">
                {props.activity.account.name}
              </span>{" "}
              created{" "}
              <Link
                to={`/deal/${dealId}/tasks/${props.activity.task.id}`}
                className="font-semibold text-persian-800 hover:text-persian-700"
              >
                {props.activity.task.title}
              </Link>
            </p>
          </div>
          <time
            title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            dateTime={formatISO(
              secondsToMilliseconds(props.activity.createdAt),
            )}
            className="flex-none py-0.5 text-xs leading-5 text-concrete-600"
          >
            {formatDistanceToNowStrict(
              secondsToMilliseconds(props.activity.createdAt),
              { addSuffix: true },
            )}
          </time>
        </div>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.TaskUpdated) {
    if (!props.activity.task) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1 items-center">
        <div
          className={classNames(
            props.isLast ? "h-6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <PencilSquareIcon className="w-4 h-4 text-persian-600" />
        </div>
        <div className="flex justify-between w-full items-center">
          <p className="text-sm leading-5 text-persian-990">
            <span className="font-semibold text-persian-990">
              {props.activity.account.name}
            </span>{" "}
            updated{" "}
            <Link
              to={`/deal/${dealId}/tasks/${props.activity.task.id}`}
              className="font-semibold text-persian-800 hover:text-persian-700"
            >
              {props.activity.task.title}
            </Link>
          </p>
          <time
            title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            dateTime={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            className="flex-none text-xs leading-5 text-concrete-600"
          >
            {formatDistanceToNowStrict(
              secondsToMilliseconds(props.activity.createdAt),
              { addSuffix: true },
            )}
          </time>
        </div>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.TaskAssigned) {
    if (!props.activity.task) {
      return null;
    }

    const assignedIds = props.activity.taskActivity?.assignedToIds ?? [];
    const assigned =
      assignedIds.length > 0
        ? props.activity.task.assignedTo.filter((a) => assignedIds.includes(a.id))
        : props.activity.task.assignedTo;

    return (
      <li key={props.activity.id} className="relative flex gap-x-1 items-center">
        <div
          className={classNames(
            props.isLast ? "h-6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <UserIcon className="w-4 h-4 text-persian-600" />
        </div>
        <div className="flex justify-between w-full items-center">
          <p className="text-sm leading-5 text-persian-990">
            <span className="font-semibold text-persian-990">
              {props.activity.account.name}
            </span>{" "}
            updated assignees for{" "}
            <Link
              to={`/deal/${dealId}/tasks/${props.activity.task.id}`}
              className="font-semibold text-persian-800 hover:text-persian-700"
            >
              {props.activity.task.title}
            </Link>
          </p>
          <time
            title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            dateTime={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            className="flex-none text-xs leading-5 text-concrete-600"
          >
            {formatDistanceToNowStrict(
              secondsToMilliseconds(props.activity.createdAt),
              { addSuffix: true },
            )}
          </time>
        </div>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.TaskDueDate) {
    if (!props.activity.task) {
      return null;
    }

    const dueDate = props.activity.taskActivity?.dueDate;

    return (
      <li key={props.activity.id} className="relative flex gap-x-1 items-center">
        <div
          className={classNames(
            props.isLast ? "h-6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <CalendarDaysIcon className="w-4 h-4 text-persian-600" />
        </div>
        <div className="flex justify-between w-full items-center">
          <p className="text-sm leading-5 text-persian-990">
            <span className="font-semibold text-persian-990">
              {props.activity.account.name}
            </span>{" "}
            {dueDate ? "updated due date for" : "removed due date for"}{" "}
            <Link
              to={`/deal/${dealId}/tasks/${props.activity.task.id}`}
              className="font-semibold text-persian-800 hover:text-persian-700"
            >
              {props.activity.task.title}
            </Link>
          </p>
          <time
            title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            dateTime={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            className="flex-none text-xs leading-5 text-concrete-600"
          >
            {formatDistanceToNowStrict(
              secondsToMilliseconds(props.activity.createdAt),
              { addSuffix: true },
            )}
          </time>
        </div>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.TaskStatusUpdated) {
    if (!props.activity.task || !props.activity.taskActivity?.status) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1 items-center">
        <div
          className={classNames(
            props.isLast ? "h-6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <StatusIcon status={props.activity.taskActivity.status} />
        </div>
        <div className="flex justify-between w-full items-center">
          <p className="text-sm leading-5 text-persian-990">
            <span className="font-semibold text-persian-990">
              {props.activity.account.name}
            </span>{" "}
            updated{" "}
            <Link
              to={`/deal/${dealId}/tasks/${props.activity.task.id}`}
              className="font-semibold text-persian-800 hover:text-persian-700"
            >
              {props.activity.task.title}
            </Link>{" "}
            to{" "}
            <span className="font-semibold">
              {formatEnum(props.activity.taskActivity.status)}
            </span>
          </p>
          <time
            title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            dateTime={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            className="flex-none text-xs leading-5 text-concrete-600"
          >
            {formatDistanceToNowStrict(
              secondsToMilliseconds(props.activity.createdAt),
              { addSuffix: true },
            )}
          </time>
        </div>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.TaskPriorityUpdated) {
    if (!props.activity.task || !props.activity.taskActivity?.priority) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1 items-center">
        <div
          className={classNames(
            props.isLast ? "h-6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <TaskPriorityIcon priority={props.activity.taskActivity.priority} />
        </div>
        <div className="flex justify-between w-full items-center">
          <p className="text-sm leading-5 text-persian-990">
            <span className="font-semibold text-persian-990">
              {props.activity.account.name}
            </span>{" "}
            {props.activity.taskActivity.priority === TaskPriority.None ? (
              <>
                removed the priority for{" "}
                <Link
                  to={`/deal/${dealId}/tasks/${props.activity.task.id}`}
                  className="font-semibold text-persian-800 hover:text-persian-700"
                >
                  {props.activity.task.title}
                </Link>
              </>
            ) : (
              <>
                updated the priority of{" "}
                <Link
                  to={`/deal/${dealId}/tasks/${props.activity.task.id}`}
                  className="font-semibold text-persian-800 hover:text-persian-700"
                >
                  {props.activity.task.title}
                </Link>{" "}
                to{" "}
                <span className="font-semibold">
                  {formatEnum(props.activity.taskActivity.priority)}
                </span>
              </>
            )}
          </p>
          <time
            title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            dateTime={formatISO(secondsToMilliseconds(props.activity.createdAt))}
            className="flex-none text-xs leading-5 text-concrete-600"
          >
            {formatDistanceToNowStrict(
              secondsToMilliseconds(props.activity.createdAt),
              { addSuffix: true },
            )}
          </time>
        </div>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.Comment) {
    if (!props.activity.comment) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h-6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <MessageCircleIcon className="w-4 h-4 text-persian-600" />
        </div>
        <div className="flex-auto min-w-0">
          <div className="flex justify-between w-full items-center">
            <p className="text-sm leading-5 text-persian-990 truncate">
              <span className="font-semibold text-persian-990">
                {props.activity.account.name}
              </span>{" "}
              commented
              {props.mode === "verbose" ? (
                <span>
                  {" "}
                  on <CommentNode activity={props.activity} />
                </span>
              ) : null}
            </p>
            <time
              title={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600 ml-2"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
          <p className={classNames(
            "text-sm leading-5 text-concrete-700 mt-0.5",
            props.mode === "verbose" ? "line-clamp-2" : "",
          )} >
            {renderMentions(props.activity.comment.comment)}
          </p>
          {props.activity.comment.files.length > 0 && (
            <div className="mt-1">
              <Pills>
                {props.activity.comment.files.map((file) => (
                  <FilePill
                    id={file.id}
                    key={file.id}
                    name={file.name}
                    type={file.fileType}
                  />
                ))}
              </Pills>
            </div>
          )}
        </div>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.FileViewed) {
    if (!props.activity.file) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <EyeIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 ">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                viewed
              </p>
              <FileLink
                id={props.activity.file.id}
                name={props.activity.file.name}
                folderId={props.activity.file.folderId}
              />
              {props.activity.mergedFile ? (
                <span className="text-xs text-white bg-blue-500 rounded-full px-2 py-0.5 mr-2">
                  Activity from merged file
                </span>
              ) : null}
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.DataRoomFileErrorResolved) {
    if (!props.activity.dataRoomFileError) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <CheckCircleIcon className="w-4 h-4 text-green-500/80" />
          </div>

          {props.activity.dataRoomFileError.resolvedMessage ? (
            <Link
              className="flex-auto rounded-md p-3 ring-1 ring-inset ring-concrete-200 hover:ring-concrete-300 bg-white/50"
              to={`/errors/${props.activity.dataRoomFileError.id}`}
            >
              <div className="flex justify-between gap-x-4">
                <div className="py-0.5 text-xs leading-5 text-concrete-600">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  marked{" "}
                  <span className="font-medium">
                    {props.activity.dataRoomFileError.key}
                  </span>{" "}
                  as resolved
                </div>
                <time
                  title={formatISO(
                    secondsToMilliseconds(props.activity.createdAt),
                  )}
                  dateTime={formatISO(
                    secondsToMilliseconds(props.activity.createdAt),
                  )}
                  className="flex-none py-0.5 text-xs leading-5 text-concrete-600"
                >
                  {formatDistanceToNowStrict(
                    secondsToMilliseconds(props.activity.createdAt),
                    { addSuffix: true },
                  )}
                </time>
              </div>
              <div className="flex items-center gap-x-2">
                <p className="text-sm leading-6 text-concrete-700 font-medium">
                  {props.activity.dataRoomFileError.resolvedMessage}
                </p>
              </div>
            </Link>
          ) : (
            <Link
              className="flex-auto"
              to={`/errors/${props.activity.dataRoomFileError.id}`}
            >
              <div className="flex justify-between gap-x-4">
                <div className="py-0.5 text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  marked{" "}
                  <span className="font-medium">
                    {props.activity.dataRoomFileError.key}
                  </span>{" "}
                  as resolved
                </div>
                <time
                  title={formatISO(
                    secondsToMilliseconds(props.activity.createdAt),
                  )}
                  dateTime={formatISO(
                    secondsToMilliseconds(props.activity.createdAt),
                  )}
                  className="flex-none py-0.5 text-xs leading-5 text-concrete-600"
                >
                  {formatDistanceToNowStrict(
                    secondsToMilliseconds(props.activity.createdAt),
                    { addSuffix: true },
                  )}
                </time>
              </div>
            </Link>
          )}
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.DataRoomFileErrorDismissed) {
    if (!props.activity.dataRoomFileError) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <NoSymbolIcon className="w-4 h-4 text-concrete-600" />
          </div>

          <Link
            className="flex-auto rounded-md p-3 ring-1 ring-inset ring-concrete-200 hover:ring-concrete-300 bg-white/50"
            to={`/errors/${props.activity.dataRoomFileError.id}`}
          >
            <div className="flex justify-between gap-x-4">
              <div className="py-0.5 text-xs leading-5 text-concrete-600">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                ignored the error for{" "}
                <span className="font-medium">
                  {props.activity.dataRoomFileError.key}
                </span>
              </div>
              <time
                title={formatISO(
                  secondsToMilliseconds(props.activity.createdAt),
                )}
                dateTime={formatISO(
                  secondsToMilliseconds(props.activity.createdAt),
                )}
                className="flex-none py-0.5 text-xs leading-5 text-concrete-600"
              >
                {formatDistanceToNowStrict(
                  secondsToMilliseconds(props.activity.createdAt),
                  { addSuffix: true },
                )}
              </time>
            </div>
            <div className="flex items-center gap-x-2">
              <p className="text-sm leading-6 text-concrete-700 font-medium">
                {props.activity.dataRoomFileError.dismissalReason}
              </p>
            </div>
          </Link>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.DataRoomFileErrorCreated) {
    if (!props.activity.dataRoomFileError) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          {props.mode === "verbose" ? (
            <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
              <ExclamationTriangleIcon className="w-4 h-4 text-orange-700/80" />
            </div>
          ) : (
            <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
              <div className="h-1.5 w-1.5 rounded-full bg-persian-300 ring-1 ring-persian-400" />
            </div>
          )}

          {props.mode === "verbose" ? (
            <Link
              className="flex-auto rounded-md p-3 ring-1 ring-inset ring-concrete-200 hover:ring-concrete-300 bg-white/50"
              to={`/errors/${props.activity.dataRoomFileError.id}`}
            >
              <div className="flex justify-between gap-x-4">
                <div className="py-0.5 text-xs leading-5 text-concrete-600">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  detected an error
                </div>
                <time
                  title={formatISO(
                    secondsToMilliseconds(props.activity.createdAt),
                  )}
                  dateTime={formatISO(
                    secondsToMilliseconds(props.activity.createdAt),
                  )}
                  className="flex-none py-0.5 text-xs leading-5 text-concrete-600"
                >
                  {formatDistanceToNowStrict(
                    secondsToMilliseconds(props.activity.createdAt),
                    { addSuffix: true },
                  )}
                </time>
              </div>
              <div className="flex items-center gap-x-2">
                <p className="text-sm leading-6 text-concrete-700 font-medium">
                  {props.activity.dataRoomFileError.key} in
                </p>
                <FilePill
                  id={props.activity.dataRoomFileError.files[0].file.id}
                  name={props.activity.dataRoomFileError.files[0].file.name}
                  type={props.activity.dataRoomFileError.files[0].file.fileType}
                />
                <div className="bg-white shadow-sm relative flex items-center rounded-2xl border px-2 py-1">
                  <p className="text-xs text-concrete-800">
                    + {props.activity.dataRoomFileError.files.length - 1} other
                    files
                  </p>
                </div>
              </div>
            </Link>
          ) : (
            <div className="flex justify-between w-full items-center">
              <div className="flex items-center  gap-x-2 -py-0.5">
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  detected an error
                </p>
              </div>
              <time
                title={formatISO(
                  secondsToMilliseconds(props.activity.createdAt),
                )}
                dateTime={formatISO(
                  secondsToMilliseconds(props.activity.createdAt),
                )}
                className="flex-none text-xs leading-5 text-concrete-600"
              >
                {formatDistanceToNowStrict(
                  secondsToMilliseconds(props.activity.createdAt),
                  { addSuffix: true },
                )}
              </time>
            </div>
          )}
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.InviteCreated) {
    if (!props.activity.invite) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <UserIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                sent a new invite to{" "}
                <span className="font-semibold text-persian-990">
                  {props.activity.invite?.email}
                </span>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.GroupCreated) {
    if (!props.activity?.accessActivity?.dealGroup) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <UserIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                created a new group{" "}
                <span className="font-semibold text-persian-990">
                  {props.activity.accessActivity.dealGroup?.name}
                </span>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (
    props.activity.type === DealActivityType.GroupBlocked ||
    props.activity.type === DealActivityType.GroupUnblocked
  ) {
    if (!props.activity?.accessActivity?.dealGroup) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            {props.activity.type === DealActivityType.GroupBlocked ? (
              <LockClosedIcon className="w-4 h-4 text-persian-600" />
            ) : (
              <LockOpenIcon className="w-4 h-4 text-persian-600" />
            )}
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                {props.activity.type === DealActivityType.GroupBlocked
                  ? "blocked"
                  : "unblocked"}{" "}
                access to group{" "}
                <span className="font-semibold text-persian-990">
                  {props.activity.accessActivity.dealGroup?.name}
                </span>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (
    props.activity.type === DealActivityType.GroupUpdatedFileAccess ||
    props.activity.type === DealActivityType.GroupUpdatedFolderAccess
  ) {
    if (
      !props.activity?.accessActivity?.fileDifferences &&
      !props.activity?.accessActivity?.folderDifferences &&
      !props.activity?.accessActivity?.permission
    ) {
      return null;
    }

    if (props.activity.accessActivity.permission) {
      return (
        <li key={props.activity.id} className="relative flex gap-x-1">
          <div
            className={classNames(
              props.isLast ? "h6" : "-bottom-6",
              "absolute left-0 top-0 flex w-6 justify-center",
            )}
          >
            <div className="w-px bg-persian-100" />
          </div>
          <>
            <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
              <FolderIcon className="w-4 h-4 text-persian-600" />
            </div>

            <div className="flex justify-between w-full items-center">
              <div className="flex items-center  gap-x-2 -py-0.5">
                <p className=" text-sm leading-5 text-persian-990">
                  <span className="font-semibold text-persian-990">
                    {props.activity.account.name}
                  </span>{" "}
                  updated file permissions for{" "}
                  <span className="font-semibold text-persian-990">
                    {props.activity.accessActivity.dealGroup?.name}
                  </span>
                </p>
              </div>
              <time
                title={formatISO(
                  secondsToMilliseconds(props.activity.createdAt),
                )}
                dateTime={formatISO(
                  secondsToMilliseconds(props.activity.createdAt),
                )}
                className="flex-none text-xs leading-5 text-concrete-600"
              >
                {formatDistanceToNowStrict(
                  secondsToMilliseconds(props.activity.createdAt),
                  { addSuffix: true },
                )}
              </time>
            </div>
          </>
        </li>
      );
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <FolderIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                {props.activity.type === DealActivityType.GroupUpdatedFileAccess
                  ? `updated access to ${props.activity.accessActivity.fileDifferences?.updated
                    .length
                  } file${props.activity.accessActivity.fileDifferences?.updated
                    .length === 1
                    ? ""
                    : "s"
                  } for `
                  : `updated access to ${props.activity.accessActivity.folderDifferences?.updated
                    .length
                  } folder${props.activity.accessActivity.folderDifferences?.updated
                    .length === 1
                    ? ""
                    : "s"
                  } for `}{" "}
                <span className="font-semibold text-persian-990">
                  {props.activity.accessActivity.dealGroup?.name}
                </span>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (
    props.activity.type === DealActivityType.AccountBlocked ||
    props.activity.type === DealActivityType.AccountUnblocked
  ) {
    if (!props.activity?.accessActivity?.account) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            {props.activity.type === DealActivityType.AccountBlocked ? (
              <LockClosedIcon className="w-4 h-4 text-persian-600" />
            ) : (
              <LockOpenIcon className="w-4 h-4 text-persian-600" />
            )}
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                {props.activity.type === DealActivityType.AccountBlocked
                  ? "blocked"
                  : "unblocked"}{" "}
                access to account{" "}
                <span className="font-semibold text-persian-990">
                  {props.activity.accessActivity.account?.name}
                </span>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (
    props.activity.type === DealActivityType.AccountUpdatedFileAccess ||
    props.activity.type === DealActivityType.AccountUpdatedFolderAccess
  ) {
    if (
      !props.activity?.accessActivity?.fileDifferences &&
      !props.activity?.accessActivity?.folderDifferences
    ) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <FolderIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                {props.activity.type ===
                  DealActivityType.AccountUpdatedFileAccess
                  ? `updated access to ${props.activity.accessActivity.fileDifferences?.updated
                    .length
                  } file${props.activity.accessActivity.fileDifferences?.updated
                    .length === 1
                    ? ""
                    : "s"
                  } for `
                  : `updated access to ${props.activity.accessActivity.folderDifferences?.updated
                    .length
                  } folder${props.activity.accessActivity.folderDifferences?.updated
                    .length === 1
                    ? ""
                    : "s"
                  } for `}{" "}
                <span className="font-semibold text-persian-990">
                  {props.activity.accessActivity.account?.name}
                </span>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.AccountImpersonation) {
    if (!props.activity?.accountImpersonation) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <UserIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center  gap-x-2 -py-0.5">
              <p className=" text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                viewed the deal as{" "}
                <span className="font-semibold text-persian-990">
                  {props.activity.accountImpersonation.name}
                </span>
              </p>
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  if (props.activity.type === DealActivityType.DataRoomFileSimilarityAccepted) {
    if (!props.activity.file) {
      return null;
    }

    return (
      <li key={props.activity.id} className="relative flex gap-x-1">
        <div
          className={classNames(
            props.isLast ? "h6" : "-bottom-6",
            "absolute left-0 top-0 flex w-6 justify-center",
          )}
        >
          <div className="w-px bg-persian-100" />
        </div>
        <>
          <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
            <MergeIcon className="w-4 h-4 text-persian-600" />
          </div>

          <div className="flex justify-between w-full items-center">
            <div className="flex items-center gap-x-2 -py-0.5">
              <p className="text-sm leading-5 text-persian-990">
                <span className="font-semibold text-persian-990">
                  {props.activity.account.name}
                </span>{" "}
                merged another document into{" "}
              </p>
              <FileLink
                id={props.activity.file.id}
                name={props.activity.file.name}
                folderId={props.activity.file.folderId}
              />
            </div>
            <time
              title={formatISO(secondsToMilliseconds(props.activity.createdAt))}
              dateTime={formatISO(
                secondsToMilliseconds(props.activity.createdAt),
              )}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(
                secondsToMilliseconds(props.activity.createdAt),
                { addSuffix: true },
              )}
            </time>
          </div>
        </>
      </li>
    );
  }

  return null;
}

function CommentNode(props: { activity: DealActivityFragmentFragment }) {
  const { dealId } = useParams<{ dealId: string }>();

  if (props.activity.question) {
    return (
      <Link to={`/deal/${dealId}/questions/${props.activity.question.id}`}>
        <span className="font-semibold text-persian-800 hover:text-persian-700">
          {props.activity.question.title}
        </span>
      </Link>
    );
  }

  if (props.activity.task) {
    return (
      <Link to={`/deal/${dealId}/tasks/${props.activity.task.id}`}>
        <span className="font-semibold text-persian-800 hover:text-persian-700">
          {props.activity.task.title}
        </span>
      </Link>
    );
  }

  if (props.activity.file) {
    return (
      <Link
        to={`/deal/${dealId}/documents/folder/${props.activity.file.folderId}/file/${props.activity.file.id}`}
      >
        <span className="font-semibold text-persian-800 hover:text-persian-700">
          {props.activity.file.name}
        </span>
      </Link>
    );
  }

  return null;
}

function TaskPills(props: { activity: DealActivityFragmentFragment }) {
  if (!props.activity.task) {
    return null;
  }

  return (
    <Pills>

      {props.activity.task.assignedTo.map((assignedTo) => (
        <UserPill key={assignedTo.id} account={assignedTo} size="s" />
      ))}
      {props.activity.task.priority ? (
        <TaskPriorityPill priority={props.activity.task.priority} />
      ) : null}
    </Pills>
  );
}

function StatusIcon(props: { status: TaskStatus }) {
  switch (props.status) {
    case TaskStatus.Todo:
      return <CircleIcon className="w-4 h-4 text-concrete-400" />;
    case TaskStatus.Completed:
      return <CircleCheckIcon className="w-4 h-4 text-green-500" />;
    case TaskStatus.InProgress:
      return <PlayCircleIcon className="w-4 h-4 text-persian-600" />;
    default:
      return null;
  }
}

function QuestionDocumentsFoundItem({
  activity,
  isLast,
  dealId,
}: {
  activity: DealActivityFragmentFragment;
  isLast: boolean;
  dealId: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!activity.question || !activity.questionDocumentsFoundData) {
    return null;
  }

  const fileCount = activity.questionDocumentsFoundData.files.length;

  return (
    <li key={activity.id} className="relative flex gap-x-1">
      <div
        className={classNames(
          isLast ? "h6" : "-bottom-6",
          "absolute left-0 top-0 flex w-6 justify-center",
        )}
      >
        <div className="w-px bg-persian-100" />
      </div>
      <>
        <div className="relative flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
          <BotIcon className="w-4 h-4 text-persian-600" />
        </div>
        <div className="flex-auto flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="py-1 text-left"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm leading-[1.44] text-persian-990">
                <span className="font-semibold">Liquid AI</span>
                {" found "}
                <span className="font-semibold text-persian-800">
                  {fileCount} {fileCount === 1 ? "file" : "files"}
                </span>
                {" relevant to "}
                <Link
                  to={`/deal/${dealId}/questions/${activity.question.id}`}
                  className="font-semibold text-persian-800 hover:text-persian-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  {activity.question.title}
                </Link>
              </p>
              <time
                title={formatISO(secondsToMilliseconds(activity.createdAt))}
                dateTime={formatISO(secondsToMilliseconds(activity.createdAt))}
                className="text-xs leading-5 text-concrete-600"
              >
                {formatDistanceToNowStrict(
                  secondsToMilliseconds(activity.createdAt),
                  { addSuffix: true },
                )}
              </time>
            </div>
            {isExpanded && (
              <div className="flex flex-wrap gap-1 mt-2">
                {activity.questionDocumentsFoundData!.files.map((file) => (
                  <div
                    key={file.id}
                    className="inline-flex h-7 items-center gap-2 px-0.5 py-1"
                  >
                    <FilePill
                      id={file.id}
                      name={file.name}
                      type={file.fileType}
                    />
                  </div>
                ))}
              </div>
            )}

          </button>

        </div>
      </>
    </li>
  );
}
